import React, { useState, useEffect } from 'react';
import {
  DocumentTextIcon,
  ExclamationTriangleIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  ChartBarIcon,
  BellIcon,
  EyeIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { documentsAPI } from '../services/api';

const Documents = () => {
  const [expiringDocuments, setExpiringDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all, expired, critical, warning, notice
  const [daysFilter, setDaysFilter] = useState(90);
  const [stats, setStats] = useState({
    expired: 0,
    critical: 0,
    warning: 0,
    notice: 0
  });

  useEffect(() => {
    loadExpiringDocuments();
  }, [daysFilter]);

  const loadExpiringDocuments = async () => {
    setLoading(true);
    try {
      const response = await documentsAPI.getExpiringDocuments(daysFilter);
      setExpiringDocuments(response.data);
      
      // Calculate stats
      const newStats = {
        expired: response.data.filter(doc => doc.expiry_status === 'Expired').length,
        critical: response.data.filter(doc => doc.expiry_status === 'Critical').length,
        warning: response.data.filter(doc => doc.expiry_status === 'Warning').length,
        notice: response.data.filter(doc => doc.expiry_status === 'Notice').length
      };
      setStats(newStats);
    } catch (error) {
      console.error('Error loading expiring documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredDocuments = () => {
    if (filter === 'all') return expiringDocuments;
    return expiringDocuments.filter(doc => doc.expiry_status.toLowerCase() === filter);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Expired':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'Critical':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'Warning':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'Notice':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Expired':
      case 'Critical':
        return <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />;
      case 'Warning':
        return <ExclamationTriangleIcon className="w-4 h-4 text-yellow-500" />;
      case 'Notice':
        return <CalendarDaysIcon className="w-4 h-4 text-blue-500" />;
      default:
        return <DocumentTextIcon className="w-4 h-4 text-gray-500" />;
    }
  };

  const StatCard = ({ title, count, color, isActive, onClick }) => (
    <div 
      className={`cursor-pointer transition-all duration-200 ${isActive ? 'ring-2 ring-blue-500' : ''}`}
      onClick={onClick}
    >
      <div className={`bg-white p-6 rounded-lg shadow border ${color}`}>
        <div className="flex items-center">
          <div className="flex-shrink-0">
            {title === 'Expired' && <ExclamationTriangleIcon className="h-8 w-8 text-red-500" />}
            {title === 'Critical' && <ExclamationTriangleIcon className="h-8 w-8 text-red-400" />}
            {title === 'Warning' && <ExclamationTriangleIcon className="h-8 w-8 text-yellow-500" />}
            {title === 'Notice' && <CalendarDaysIcon className="h-8 w-8 text-blue-500" />}
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium truncate text-gray-500">{title}</dt>
              <dd className="text-lg font-medium text-gray-900">{count}</dd>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <DocumentTextIcon className="w-8 h-8 text-blue-600 mr-3" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Document Management</h1>
              <p className="text-gray-600">Monitor employee visa, passport, and document expiry status</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <select
              value={daysFilter}
              onChange={(e) => setDaysFilter(parseInt(e.target.value))}
              className="border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={30}>Next 30 days</option>
              <option value={60}>Next 60 days</option>
              <option value={90}>Next 90 days</option>
              <option value={180}>Next 6 months</option>
              <option value={365}>Next year</option>
            </select>
            <button 
              onClick={loadExpiringDocuments}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Expired"
          count={stats.expired}
          color="border-red-200"
          isActive={filter === 'expired'}
          onClick={() => setFilter(filter === 'expired' ? 'all' : 'expired')}
        />
        <StatCard
          title="Critical"
          count={stats.critical}
          color="border-red-100"
          isActive={filter === 'critical'}
          onClick={() => setFilter(filter === 'critical' ? 'all' : 'critical')}
        />
        <StatCard
          title="Warning"
          count={stats.warning}
          color="border-yellow-100"
          isActive={filter === 'warning'}
          onClick={() => setFilter(filter === 'warning' ? 'all' : 'warning')}
        />
        <StatCard
          title="Notice"
          count={stats.notice}
          color="border-blue-100"
          isActive={filter === 'notice'}
          onClick={() => setFilter(filter === 'notice' ? 'all' : 'notice')}
        />
      </div>

      {/* Documents List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">
              {filter === 'all' ? 'All Expiring Documents' : `${filter.charAt(0).toUpperCase() + filter.slice(1)} Documents`}
              <span className="ml-2 text-sm text-gray-500">({getFilteredDocuments().length})</span>
            </h2>
            {filter !== 'all' && (
              <button
                onClick={() => setFilter('all')}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Show All
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : getFilteredDocuments().length === 0 ? (
          <div className="text-center py-8">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No documents found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {filter === 'all' 
                ? 'No documents are expiring in the selected time period.'
                : `No documents with ${filter} status found.`
              }
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {getFilteredDocuments().map((document, index) => (
              <div key={`${document.document_id}-${index}`} className="p-6 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      {getStatusIcon(document.expiry_status)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <h3 className="text-sm font-medium text-gray-900 truncate">
                          {document.employee_name}
                        </h3>
                        <span className="text-sm text-gray-500">({document.employee_number})</span>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(document.expiry_status)}`}>
                          {document.expiry_status}
                        </span>
                      </div>
                      <div className="mt-1">
                        <p className="text-sm text-gray-900 font-medium">{document.document_type}</p>
                        <p className="text-sm text-gray-600">{document.document_name}</p>
                        {document.document_number && (
                          <p className="text-sm text-gray-500">Number: {document.document_number}</p>
                        )}
                      </div>
                      <div className="mt-2 text-sm text-gray-600">
                        <div className="flex items-center space-x-4">
                          <span>Expiry: {new Date(document.expiry_date).toLocaleDateString('en-GB')}</span>
                          <span className={document.days_until_expiry < 0 ? 'text-red-600 font-medium' : 'text-gray-600'}>
                            {document.days_until_expiry < 0 
                              ? `Expired ${Math.abs(document.days_until_expiry)} days ago`
                              : document.days_until_expiry === 0
                              ? 'Expires today'
                              : `${document.days_until_expiry} days remaining`
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        // Navigate to employee detail
                        window.location.href = `/employees?employeeId=${document.employee_id}`;
                      }}
                      className="p-2 text-gray-400 hover:text-gray-600"
                      title="View Employee"
                    >
                      <EyeIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex">
          <BellIcon className="w-5 h-5 text-blue-400 mt-0.5 mr-3" />
          <div>
            <h3 className="text-sm font-medium text-blue-800">Document Management Tips</h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li>Click on status cards to filter documents by expiry status</li>
                <li>Documents marked as "Expired" or "Critical" require immediate attention</li>
                <li>Use the employee view button to access and manage individual documents</li>
                <li>Regular monitoring helps ensure compliance with immigration and work authorization requirements</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Documents;